/**
 * Brand Kit — agency feature.
 *
 * Logged-in users can upload a logo (PNG/JPG/WEBP, max 5 MB) + a custom font
 * (TTF/OTF, max 10 MB). Both are applied during AutoCaptions render:
 *   • Logo → top-right corner overlay
 *   • Font family → caption text font
 *
 * Backend persists everything in MongoDB + R2.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, Sparkles, ImagePlus, Type, Trash2, Palette,
  CheckCircle2, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SignInRequiredCard from "./SignInRequiredCard";
import "./AutoCaptionsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AutoCaptionsBrandKitPage() {
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [authed, setAuthed] = useState(null);   // null = unknown, false/true after refresh
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(null);   // "logo" | "font" | null
  const [color, setColor] = useState("#7c3aed");
  const [savingColor, setSavingColor] = useState(false);
  const logoInput = useRef(null);
  const fontInput = useRef(null);

  const refresh = () => {
    return axios.get(`${API}/api/auto-captions/brand-kit`, {
      withCredentials: true,
    })
      .then((r) => {
        setKit(r.data || {});
        setAuthed(!!r.data?.authenticated);
        if (r.data?.brand_color) setColor(r.data.brand_color);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Could not load Brand Kit.");
        setKit({});
        setAuthed(false);
      });
  };

  useEffect(() => { refresh(); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/api/auto-captions/brand-kit/${kind}`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind === "logo" ? "Logo" : "Font"} uploaded`);
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(null);
    }
  };

  const removeItem = async (kind) => {
    try {
      await axios.delete(`${API}/api/auto-captions/brand-kit/${kind}`, { withCredentials: true });
      toast.success(`${kind === "logo" ? "Logo" : "Font"} removed`);
      await refresh();
    } catch (err) {
      toast.error("Could not remove");
    }
  };

  const saveColor = async () => {
    setSavingColor(true);
    try {
      await axios.put(`${API}/api/auto-captions/brand-kit/color`,
        { brand_color: color },
        { withCredentials: true },
      );
      toast.success("Brand color saved");
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not save color";
      toast.error(msg);
    } finally {
      setSavingColor(false);
    }
  };

  const clearColor = async () => {
    try {
      await axios.put(`${API}/api/auto-captions/brand-kit/color`,
        { brand_color: null },
        { withCredentials: true },
      );
      setColor("#7c3aed");
      toast.success("Brand color cleared");
      await refresh();
    } catch (err) {
      toast.error("Could not clear");
    }
  };

  return (
    <>
      <Header />
      <div className="ac-page" data-testid="brand-kit-page">
        <div className="ac-inner" style={{ maxWidth: 860 }}>
          <button type="button" className="ac-link" onClick={() => navigate("/tools/auto-captions")} data-testid="bk-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={12}/> Back to AutoCaptions
          </button>
          <span className="ac-eyebrow"><Sparkles size={12}/> Agency · Brand Kit</span>
          <h1 className="ac-h1">Make every export <em>yours</em>.</h1>
          <p className="ac-sub">
            Upload your logo and a custom font — we&rsquo;ll apply them automatically to every
            captioned video you render. Perfect for agencies and creators with their own look.
          </p>

          {authed === false && (
            <SignInRequiredCard
              icon={<Palette size={26} strokeWidth={1.6}/>}
              eyebrow="Agency · Brand Kit"
              title="Sign in to set up your Brand Kit"
              description="Brand Kit lets you upload your own logo, custom font and brand color so every captioned video looks like yours — automatically."
              benefits={[
                "Logo overlay on every render",
                "Custom font family for captions",
                "Brand color as default highlight",
              ]}
              testId="bk-signin-required"
            />
          )}

          {error && authed !== false && (
            <div className="ac-error" data-testid="bk-error">
              <AlertTriangle size={16}/> {error}
            </div>
          )}

          {authed === null && !error && (
            <div className="ac-card" style={{ textAlign: "center" }}>
              <Loader2 size={22} className="ac-spin" style={{ color: "var(--ac-purple)" }}/>
            </div>
          )}

          {authed === true && kit && !error && (
            <div className="bk-grid" data-testid="bk-grid">
              {/* Logo */}
              <motion.div className="bk-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <div className="bk-card-head">
                  <div className="bk-card-icon"><ImagePlus size={20}/></div>
                  <div>
                    <h3>Brand logo</h3>
                    <p>Overlaid top-right of every render. PNG / JPG / WEBP · max 5 MB.</p>
                  </div>
                </div>
                <div className="bk-preview" data-testid="bk-logo-preview">
                  {kit.logo ? (
                    <img
                      src={`${API}/api/auto-captions/brand-kit/logo-file?v=${Date.now()}`}
                      alt="Brand logo preview"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="bk-empty">No logo yet</div>
                  )}
                </div>
                <div className="bk-actions">
                  <input ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => upload("logo", e.target.files?.[0])} data-testid="bk-logo-input"/>
                  <button className="ac-btn" disabled={uploading === "logo"} onClick={() => logoInput.current?.click()} data-testid="bk-logo-upload">
                    {uploading === "logo" ? <Loader2 size={14} className="ac-spin"/> : <ImagePlus size={14}/>}
                    {kit.logo ? "Replace logo" : "Upload logo"}
                  </button>
                  {kit.logo && (
                    <button className="ac-btn ac-btn--ghost" onClick={() => removeItem("logo")} data-testid="bk-logo-remove">
                      <Trash2 size={14}/> Remove
                    </button>
                  )}
                </div>
              </motion.div>

              {/* Font */}
              <motion.div className="bk-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}>
                <div className="bk-card-head">
                  <div className="bk-card-icon"><Type size={20}/></div>
                  <div>
                    <h3>Custom font</h3>
                    <p>Used as the caption font family. TTF / OTF · max 10 MB.</p>
                  </div>
                </div>
                <div className="bk-preview bk-preview--font" data-testid="bk-font-preview">
                  {kit.font ? (
                    <div>
                      <div className="bk-font-family">{kit.font.family}</div>
                      <div className="bk-font-sample">Aa · Bb · Cc · 123 · The quick brown fox</div>
                    </div>
                  ) : (
                    <div className="bk-empty">No font yet</div>
                  )}
                </div>
                <div className="bk-actions">
                  <input ref={fontInput} type="file" accept=".ttf,.otf,font/ttf,font/otf" hidden onChange={(e) => upload("font", e.target.files?.[0])} data-testid="bk-font-input"/>
                  <button className="ac-btn" disabled={uploading === "font"} onClick={() => fontInput.current?.click()} data-testid="bk-font-upload">
                    {uploading === "font" ? <Loader2 size={14} className="ac-spin"/> : <Type size={14}/>}
                    {kit.font ? "Replace font" : "Upload font"}
                  </button>
                  {kit.font && (
                    <button className="ac-btn ac-btn--ghost" onClick={() => removeItem("font")} data-testid="bk-font-remove">
                      <Trash2 size={14}/> Remove
                    </button>
                  )}
                </div>
              </motion.div>

              {/* Brand color */}
              <motion.div className="bk-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
                <div className="bk-card-head">
                  <div className="bk-card-icon" style={{ background: color }}><Palette size={20}/></div>
                  <div>
                    <h3>Brand color</h3>
                    <p>Used as the default caption highlight color in new renders.</p>
                  </div>
                </div>
                <div className="bk-preview bk-preview--font" data-testid="bk-color-preview">
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      data-testid="bk-color-input"
                      style={{ width: 56, height: 56, border: "none", borderRadius: 12, padding: 0, background: "transparent", cursor: "pointer" }}
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      data-testid="bk-color-hex"
                      placeholder="#7c3aed"
                      style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "var(--ac-text, #fff)", fontFamily: "ui-monospace, monospace", fontSize: 14 }}
                    />
                  </div>
                  {kit.brand_color && (
                    <div style={{ color: "var(--ac-text-soft)", fontSize: 12, marginTop: 10 }}>
                      Currently saved: <strong style={{ color }}>{kit.brand_color}</strong>
                    </div>
                  )}
                </div>
                <div className="bk-actions">
                  <button className="ac-btn" disabled={savingColor} onClick={saveColor} data-testid="bk-color-save">
                    {savingColor ? <Loader2 size={14} className="ac-spin"/> : <Palette size={14}/>}
                    {kit.brand_color ? "Update color" : "Save color"}
                  </button>
                  {kit.brand_color && (
                    <button className="ac-btn ac-btn--ghost" onClick={clearColor} data-testid="bk-color-remove">
                      <Trash2 size={14}/> Clear
                    </button>
                  )}
                </div>
              </motion.div>
            </div>
          )}

          {authed === true && kit && (kit.logo || kit.font || kit.brand_color) && (
            <div className="ac-card" style={{ marginTop: 18 }}>
              <p style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                <CheckCircle2 size={16} color="#22c55e"/>
                <strong>Brand Kit is active</strong>
              </p>
              <p style={{ color: "var(--ac-text-soft)", marginTop: 6, marginBottom: 0 }}>
                Next time you render a captioned video, your{" "}
                {[kit.logo && "logo", kit.font && "font", kit.brand_color && "color"].filter(Boolean).join(" + ")} will be applied automatically.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
